import React, { useState, useEffect } from "react";
import AdminTopBar from "./AdminTopBar";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../../Services/firebase-config.js";
import { Modal } from "react-modal";
import { Bar } from "react-chartjs-2"; // Assuming you're using Chart.js
import {
  FilterIcon,
  SearchIcon,
  DocumentReportIcon,
} from "@heroicons/react/outline";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import {
  doc,
  getDocs,
  collection,
  updateDoc,
  onSnapshot,
  limit,
  query,
  where,
} from "firebase/firestore";
import { useApplicationForm } from "../../Contexts/ApplicationFormContext";

const Submissions = () => {
  const location = useLocation();
  const bureau = location.state?.bureau;
  const { userData } = useApplicationForm();
  const [submissions, setSubmissions] = useState([]);
  const [selectedBureau, setSelectedBureau] = useState(bureau);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reviewed, setReviewed] = useState(null);
  console.log(reviewed);

  useEffect(() => {
    const unsubscribe = fetchFormData();
    return unsubscribe;
  }, [selectedBureau, reviewed]);

  const fetchFormData = () => {
    const baseQuery = [
      where("submitted", "==", true),
      ...(selectedBureau ? [where("valueChain", "==", selectedBureau)] : []),
      ...(reviewed !== null ? [where("reviewed", "==", reviewed)] : []),
      // limit(10),
    ];
    const q = query(collection(db, "applications"), ...baseQuery);

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const submissionsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubmissions(submissionsList);
      } else {
        setSubmissions([]);
      }
    });

    return unsubscribe;
  };

  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortDirection, setSortDirection] = useState("ascending");
  const navigate = useNavigate(); // To handle navigation

  const bureaus = [
    "Pathogen Economy",
    "Mobility",
    "Infrastructure Innovations",
    "Industry 4.0+",
    "Aeronautics and Space",
    "Productivity Acceleration",
    "STI Support Services",
  ];

  const handleUpdate = async (id, key, value) => {
    await updateDoc(doc(db, "applications", id), {
      [key]: value,
    });
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle sorting by innovation title
  const handleSort = () => {
    const sortedSubmissions = [...submissions].sort((a, b) => {
      if (sortDirection === "ascending") {
        return a.title.localeCompare(b.title);
      } else {
        return b.title.localeCompare(a.title);
      }
    });
    setSortDirection(
      sortDirection === "ascending" ? "descending" : "ascending"
    );
    setSubmissions(sortedSubmissions);
  };

  const filteredSubmissions = submissions.filter((submission) => {
    if (filter === "reviewed" && !submission.reviewed) return false;
    if (filter === "not-reviewed" && submission.reviewed) return false;
    if (
      searchTerm &&
      !submission.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
      return false;
    return true;
  });

  const handleRowClick = (submission) => {
    navigate(`/submissions/${submission.id}`, { state: { submission } });
  };

  const toSentenceCase = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const openModal = (bureau) => {
    setSelectedBureau(bureau);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  );
  // Example Bar Chart Data
  const chartData = {
    labels: submissions.map((submission) => submission.title),
    datasets: [
      {
        label: "Scores",
        data: submissions.map((submission) => submission.score || 0),
        backgroundColor: "#3b82f6", // Blue color
      },
    ],
  };

  // Handle printing
  const handlePrint = () => {
    window.print();
  };

  // Example export to CSV
  const handleExport = () => {
    const csvData = submissions.map((submission) => ({
      title: submission.title,
      comments: submission.comments || "No comments",
      score: submission.score || "Not Reviewed",
    }));

    // Convert CSV data to a string and download
    const csvContent =
      "data:text/csv;charset=utf-8," +
      ["Title,Comments,Score"].join(",") +
      "\n" +
      csvData.map((row) => Object.values(row).join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "submissions_report.csv");
    document.body.appendChild(link);
    link.click();
  };

  return (
    <>
      <AdminTopBar title="Submissions" />

      <div className="container mx-auto p-6 space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 space-y-4 md:space-y-0">
          {/* Search Bar on the Left */}
          <div className="flex items-center border border-gray-300 p-2 transition-all focus-within:ring-2 focus-within:ring-blue-500">
            <SearchIcon className="w-5 h-5 text-gray-500 mr-2" />
            <input
              type="text"
              className="w-40 md:w-64 transition-all focus:outline-none"
              placeholder="Search submissions..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          {/* Right-aligned Filters and Button */}
          <div className="flex items-center ml-auto space-x-4">
            {/* Filter by All, Reviewed, Not Reviewed */}
            <select
              className="border border-gray-300 p-2"
              value={reviewed ?? ""}
              onChange={(e) => setReviewed(e.target.value || null)}
            >
              <option value="">All Submissions</option>
              <option value="true">Reviewed</option>
              <option value="false">Not Reviewed</option>
            </select>

            {/* Bureau Filter */}
            {userData?.role === "Administrator" && (
              <select
                className="border border-gray-300 p-2 focus:ring-2 focus:ring-blue-500"
                value={selectedBureau ?? ""}
                // disabled={userData?.role !== "Administator"}
                onChange={(e) => setSelectedBureau(e.target.value)}
              >
                <option value="">Filter by Bureau</option>
                {bureaus.map((bureau, index) => (
                  <option key={index} value={bureau}>
                    {bureau}
                  </option>
                ))}
              </select>
            )}

            {/* Report Button with Icon */}
            {userData?.role === "Administrator" && (
              <button
                className="flex items-center bg-custom-blue-1 text-white p-2 hover:bg-blue-600 transition duration-300 ease-in-out"
                onClick={() => openModal(selectedBureau)}
                disabled={!selectedBureau} // Disable button if no bureau is selected
              >
                <DocumentReportIcon className="w-5 h-5 mr-2" />
                <span>View Report</span>
              </button>
            )}
          </div>
        </div>

        <table className="min-w-full bg-white border border-gray-300">
          <thead>
            <tr>
              <th className="py-2 px-4 border-b text-center">No</th>
              <th
                className="py-2 px-4 border-b cursor-pointer"
                onClick={handleSort}
              >
                Innovation Title {sortDirection === "ascending" ? "↑" : "↓"}
              </th>
              <th className="py-2 px-4 border-b text-center">Comment</th>
              <th className="py-2 px-4 border-b text-center">Score</th>
              {userData?.role === "Administrator" && (
                <th className="py-2 px-4 border-b text-center">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredSubmissions.map((submission, index) => (
              <tr
                key={index}
                onClick={() => handleRowClick(submission)}
                className="cursor-pointer hover:bg-gray-100"
              >
                <td className="py-2 px-4 border-b">{index + 1}</td>
                <td className="py-2 px-4 border-b">{submission.title}</td>

                <td className="py-2 px-4 border-b">
                  {submission.comments || "No Comment"}
                </td>

                <td className="py-2 px-4 border-b">
                  {submission.reviewed ? (
                    submission.score
                  ) : (
                    <span className="text-red-600">Not Reviewed</span>
                  )}
                </td>

                {userData?.role === "Administrator" && (
                  <td className="py-2 px-4 border-b">
                    <select
                      className="border border-gray-300 p-2 "
                      value={submission.valueChain}
                      onChange={(e) =>
                        handleUpdate(
                          submission.id,
                          "valueChain",
                          e.target.value
                        )
                      }
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="" disabled>
                        Forward to Bureau
                      </option>
                      {bureaus.map((bureau, i) => (
                        <option key={i} value={bureau}>
                          {bureau}
                        </option>
                      ))}
                    </select>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal for Department/Bureau Report */}
      {isModalOpen && (
        <Modal isOpen={isModalOpen} onRequestClose={closeModal}>
          <div className="bg-white p-6  shadow-md w-full max-w-2xl mx-auto">
            <h2 className="text-xl font-bold mb-4">{selectedBureau} Report</h2>

            {/* Chart showing submission scores */}
            <Bar data={chartData} />

            {/* Table of submissions */}
            <table className="min-w-full bg-white border border-gray-300 mt-6">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Title</th>
                  <th className="py-2 px-4 border-b">Score</th>
                  <th className="py-2 px-4 border-b">Comments</th>
                </tr>
              </thead>
              <tbody>
                {submissions
                  .filter((submission) => submission.bureau === selectedBureau)
                  .map((submission, index) => (
                    <tr key={index}>
                      <td className="py-2 px-4 border-b">{submission.title}</td>
                      <td className="py-2 px-4 border-b">
                        {submission.score || "Not Reviewed"}
                      </td>
                      <td className="py-2 px-4 border-b">
                        {submission.comments || "No Comment"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {/* Print and Export Buttons */}
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={handlePrint}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                Print Report
              </button>
              <button
                onClick={handleExport}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Export as CSV
              </button>
            </div>

            <button
              onClick={closeModal}
              className="mt-4 text-gray-600 hover:text-red-600"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};
export default Submissions;
